<script setup>
import { ref, computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import SettingsTabs from '@/Components/UI/SettingsTabs.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import RatePlanFormModal from '@/Components/RatePlans/RatePlanFormModal.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  rate_plans: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  vehicles:   { type: Array, default: () => [] },
})

const { t } = useI18n()
const { formatMoney } = useCurrency()
const page = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')

const modalOpen = ref(false)
const editing   = ref(null)
const confirmDeleteId = ref(null)

const categoryPlans = computed(() => props.rate_plans.filter(p => p.category))
const vehiclePlans  = computed(() => props.rate_plans.filter(p => !p.category))

const openCreate = () => { editing.value = null; modalOpen.value = true }
const openEdit   = (p) => { editing.value = p; modalOpen.value = true }
const closeModal = () => { modalOpen.value = false; editing.value = null }

const askDelete = (p) => { confirmDeleteId.value = p.id }
const confirmDelete = () => {
  router.delete(`/rate_plans/${confirmDeleteId.value}`, { preserveScroll: true })
  confirmDeleteId.value = null
}
const cancelDelete = () => { confirmDeleteId.value = null }

const fmtDuration = (days) => {
  if (days % 30 === 0) return t('ratePlans.fmt.month', { n: days / 30 })
  if (days % 7  === 0) return t('ratePlans.fmt.week',  { n: days / 7 })
  return t('ratePlans.fmt.day', { n: days })
}
</script>

<template>
  <PageShell :title="t('settings.title')">
    <template #actions>
      <UiButton v-if="isAdmin" variant="primary" icon-left="plus" @click="openCreate">
        {{ t('ratePlans.new') }}
      </UiButton>
    </template>

    <SettingsTabs />

    <div class="space-y-6">
      <section>
        <h2 class="text-sm font-semibold text-[color:var(--inkSoft)] mb-2">
          {{ t('ratePlans.categoryPlans') }}
        </h2>
        <div class="space-y-2">
          <UiCard v-for="p in categoryPlans" :key="p.id">
            <div class="flex items-start justify-between gap-3 flex-wrap">
              <div class="min-w-0">
                <h3 class="font-display text-base text-[color:var(--ink)]">{{ p.name }}</h3>
                <p class="text-sm text-[color:var(--inkSoft)] mt-0.5">
                  {{ fmtDuration(p.duration_days) }} · {{ formatMoney(p.total_cents) }}
                  <span class="text-xs text-[color:var(--muted)] ml-1">({{ formatMoney(p.daily_rate_cents) }}/day)</span>
                </p>
                <p class="text-xs text-[color:var(--muted)] mt-0.5 capitalize">{{ p.category }}</p>
              </div>
              <div v-if="isAdmin" class="flex items-center gap-2">
                <UiButton size="sm" variant="secondary" @click="openEdit(p)">{{ t('common.edit') }}</UiButton>
                <UiButton size="sm" variant="ghost" @click="askDelete(p)">{{ t('common.delete') }}</UiButton>
              </div>
            </div>
          </UiCard>
          <p v-if="!categoryPlans.length" class="text-sm text-[color:var(--muted)] py-2">
            {{ t('ratePlans.empty') }}
          </p>
        </div>
      </section>

      <section>
        <h2 class="text-sm font-semibold text-[color:var(--inkSoft)] mb-2">
          {{ t('ratePlans.vehiclePlans') }}
        </h2>
        <div class="space-y-2">
          <UiCard v-for="p in vehiclePlans" :key="p.id">
            <div class="flex items-start justify-between gap-3 flex-wrap">
              <div class="min-w-0">
                <h3 class="font-display text-base text-[color:var(--ink)]">{{ p.name }}</h3>
                <p class="text-sm text-[color:var(--inkSoft)] mt-0.5">
                  {{ fmtDuration(p.duration_days) }} · {{ formatMoney(p.total_cents) }}
                  <span class="text-xs text-[color:var(--muted)] ml-1">({{ formatMoney(p.daily_rate_cents) }}/day)</span>
                </p>
                <p class="text-xs text-[color:var(--muted)] mt-0.5 truncate">
                  {{ p.vehicle_labels.join(', ') || t('ratePlans.noAssignments') }}
                </p>
              </div>
              <div v-if="isAdmin" class="flex items-center gap-2">
                <UiButton size="sm" variant="secondary" @click="openEdit(p)">{{ t('common.edit') }}</UiButton>
                <UiButton size="sm" variant="ghost" @click="askDelete(p)">{{ t('common.delete') }}</UiButton>
              </div>
            </div>
          </UiCard>
          <p v-if="!vehiclePlans.length" class="text-sm text-[color:var(--muted)] py-2">
            {{ t('ratePlans.empty') }}
          </p>
        </div>
      </section>
    </div>

    <RatePlanFormModal
      :open="modalOpen"
      :plan="editing"
      :categories="categories"
      :vehicles="vehicles"
      @close="closeModal"
    />

    <ConfirmDialog
      v-if="confirmDeleteId"
      :open="!!confirmDeleteId"
      :title="t('ratePlans.deleteConfirmTitle')"
      :consequence="t('ratePlans.deleteConfirm')"
      danger
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </PageShell>
</template>
